import React from 'react';
import { ARCHETYPE_META, ArchetypeIcon } from './NeuralProfile';

const DIMENSION_LABELS = {
  rawSpeed: 'Raw Speed',
  decisionQuality: 'Decision Quality',
  aimPrecision: 'Aim Precision',
  trackingAccuracy: 'Tracking Accuracy',
  workingMemory: 'Working Memory',
  flickSpeed: 'Flick Speed',
  prioritization: 'Prioritization',
  consistency: 'Consistency',
};

function getBarColor(score) {
  if (score >= 75) return '#00d4ff';
  if (score >= 50) return '#a78bfa';
  if (score >= 25) return '#f59e0b';
  return '#ef4444';
}

const GENRE_COLORS = {
  general: '#00d4ff',
  fps: '#ef4444',
  moba: '#a855f7',
};

export default function ShareImageCard({ profile, shareImageRef }) {
  const meta = ARCHETYPE_META[profile.archetype?.id] || { color: '#8b9ab5', icon: '◈' };
  const { scores } = profile;

  const dimEntries = Object.entries(scores).filter(([k]) => k !== 'overallScore');
  const overallScore = scores.overallScore !== undefined
    ? scores.overallScore
    : Math.round(dimEntries.reduce((a, [, v]) => a + v, 0) / dimEntries.length);

  const genreColor = GENRE_COLORS[profile.genre] || '#00d4ff';
  const profileUrl = `neurorank.app/profile/${profile.shareableId}`;

  return (
    <div
      ref={shareImageRef}
      style={{
        position: 'fixed',
        left: -9999,
        top: 0,
        width: 1200,
        height: 630,
        background: '#0d1421',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        overflow: 'hidden',
      }}
    >
      {/* Top accent bar */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 4,
        background: `linear-gradient(90deg, ${meta.color}, transparent)`,
      }} />

      {/* Background radial glow top-right */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: 400,
        height: 400,
        background: `radial-gradient(circle at top right, ${meta.color}22, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      {/* Left column */}
      <div style={{
        position: 'absolute',
        left: 60,
        top: 60,
        width: 540,
        bottom: 60,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}>
        <div>
          {/* NEURORANK label */}
          <div style={{
            fontSize: 11,
            color: '#00d4ff',
            letterSpacing: 5,
            fontWeight: 700,
            textTransform: 'uppercase',
            marginBottom: 20,
          }}>
            NEURORANK
          </div>

          {/* Player tag */}
          <div style={{
            fontSize: 48,
            fontWeight: 900,
            color: '#f8fafc',
            lineHeight: 1.1,
            marginBottom: 20,
            letterSpacing: -1,
          }}>
            {profile.playerTag}
          </div>

          {/* Archetype badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: meta.color + '18',
              border: `1px solid ${meta.color}55`,
              borderRadius: 20,
              padding: '6px 14px',
              fontSize: 14,
              fontWeight: 700,
              color: meta.color,
            }}>
              <ArchetypeIcon archetypeId={profile.archetype?.id} size={14} /> {profile.archetype?.name}
            </div>

            {/* Genre pill */}
            {profile.genre && (
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 1.2,
                padding: '6px 12px',
                borderRadius: 20,
                border: `1px solid ${genreColor}44`,
                color: genreColor,
                textTransform: 'uppercase',
              }}>
                {profile.genre}
              </div>
            )}
          </div>

          {/* Archetype tagline */}
          {profile.archetype?.tagline && (
            <div style={{
              fontSize: 14,
              color: '#6b7ab8',
              fontStyle: 'italic',
              marginBottom: 12,
            }}>
              {profile.archetype.tagline}
            </div>
          )}
        </div>

        {/* Profile URL at bottom */}
        <div style={{
          fontSize: 12,
          color: '#3d4f6b',
          fontFamily: 'monospace',
        }}>
          {profileUrl}
        </div>
      </div>

      {/* Vertical divider */}
      <div style={{
        position: 'absolute',
        left: 660,
        top: 40,
        bottom: 40,
        width: 1,
        background: '#1f2d3d',
      }} />

      {/* Right column */}
      <div style={{
        position: 'absolute',
        left: 680,
        right: 60,
        top: 60,
        bottom: 60,
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Overall score centered */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 11, color: '#6b7ab8', letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>
            Overall Score
          </div>
          <div style={{ fontSize: 72, fontWeight: 900, color: meta.color, lineHeight: 1 }}>
            {overallScore}
          </div>
          <div style={{ fontSize: 16, color: '#3d4f6b', marginTop: 2 }}>/100</div>
        </div>

        {/* Dimension bars */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
          {dimEntries.map(([key, val]) => {
            const barColor = getBarColor(val);
            return (
              <div key={key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, color: '#8899b0' }}>{DIMENSION_LABELS[key] || key}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: barColor }}>{val}</span>
                </div>
                <div style={{ height: 5, background: '#1f2d3d', borderRadius: 3 }}>
                  <div style={{
                    height: '100%',
                    width: `${val}%`,
                    borderRadius: 3,
                    background: barColor,
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
